import {
  type BreakView,
  type GanttObject,
  GanttObjectType,
  type GanttUpdate,
  type QueueView,
} from '@nexelem/graphne-api';
import { v4 as uuidv4 } from 'uuid';
import {
  type GanttQueue,
  NodeType,
  type QueueBatchNode,
  type QueueBreakNode,
  type QueueTaskNode,
} from '../Gantt/types';
import { getQueueStatusColor } from './index';

type Range = { startDate: Date; endDate: Date };

// nodeWidth will be calculated in generateQueues
const nodeWidth = 0;

export const mapSSEDataToGanttQueues = (data: GanttUpdate, range: Range): GanttQueue[] => {
  if (!data) {
    return [];
  }

  try {
    if (data.productionQueues?.length) {
      return data.productionQueues.map<GanttQueue>(({ queue, tasks = [] }) => {
        const { breaks = [], id, status } = queue;
        return {
          id,
          status: getQueueStatusColor(status),
          shortName: queue.shortName,
          name: queue.name,
          area: queue.area,
          queueNodes: [...breaks.map(mapBreakNodes(range, queue)), ...tasks.map(mapTaskNodes)],
        };
      });
    }
    return [];
  } catch (e) {
    console.error(e);
    return [];
  }
};

const mapBreakNodes =
  (range: Range, queue: QueueView) =>
  ({ start, end }: BreakView): QueueBreakNode => {
    return {
      type: NodeType.BREAK,
      id: uuidv4(),
      startDate: start ? new Date(start) : range.startDate,
      endDate: end ? new Date(end) : range.endDate,
      nodeWidth,
      queue,
    };
  };

const mapTaskNodes = (task: GanttObject, index: number, tasks: GanttObject[]): QueueBatchNode | QueueTaskNode => {
  const data = {
    endDate: new Date(task.end),
    startDate: new Date(task.start),
    nodeWidth,
  };

  if (task.tpe === GanttObjectType.Task) {
    return {
      ...data,
      ...task,
      type: NodeType.TASK,
      isPreviousProductEqual: calculateProductNameEquality(task, tasks[index - 1]),
      isNextProductEqual: calculateProductNameEquality(task, tasks[index + 1]),
    };
  }

  return {
    ...data,
    ...task,
    type: NodeType.BATCH,
    isPreviousProductEqual: calculateProductNameEquality(task, tasks[index - 1]),
    isNextProductEqual: calculateProductNameEquality(task, tasks[index + 1]),
  };
};

const calculateProductNameEquality = (task0: GanttObject, task1?: GanttObject): boolean => {
  if (task0.tpe === GanttObjectType.Task && task1?.tpe === GanttObjectType.Task) {
    return task0.productName === task1.productName;
  }

  if (task0.tpe === GanttObjectType.Batch && task1?.tpe === GanttObjectType.Batch) {
    return (
      task0.tasks
        ?.map((el) => el.productName)
        .toSorted()
        .join(', ') ===
      task1.tasks
        ?.map((el) => el.productName)
        .toSorted()
        .join(', ')
    );
  }

  return false;
};
